import React, { useState } from 'react';
import styled from 'styled-components';
import Button, { ButtonType } from '~/components/shared/Button/button';
import Image from '~/components/shared/Image/Image';
import {
  FontWeights,
  Typography,
} from '~/components/shared/Typography/Typography';
import { InvoiceStatuses } from '~/enums';
import useTranslate from '~/hooks/useTranslate';
import { theme } from '~/styles/themes';
import { SingleInvoice } from '~/Interfaces';
import dayjs from 'dayjs';
import { MediaQueries } from '~/constants/constVars';
import { formatAmount } from '~/helpers/formatters.helper';
import { StyledIcon, SvgSrc } from '~/components/shared/Icon/Icon';
import InvoicePDF from './InvoicePDF';
import Card from './PaymentMethods/Instalments/Card';

export const InvoiceBodyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow: 0 0.25em 1em -0.5em ${theme.colors.dark};
  border-radius: 0.25em;
  margin-bottom: 1em;
  padding: 0em 2em;
`;

const InvoiceImageHoder = styled.div`
  width: 5em;
  text-align: center;
  @media ${MediaQueries.bigScreen} {
    max-width: 7em;
  }
  @media (orientation: landscape) {
    width: 10em;
  }
`;

export const InvoiceBodyInfoHolder = styled.div<BorderProps>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1em 0em 1em 0em;
  border-top: ${(props) => props.width}px solid ${theme.colors.backdrop};
  border-bottom: ${(props) => props.width}px solid ${theme.colors.backdrop};
`;

const InoviceBodyButtonHolder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2em 0em 1em 0em;
`;

export const InvoiceBodyTextHolder = styled.div`
  display: flex;
  flex-direction: column;
`;

const PayLaterHolder = styled.div`
  display: flex;
  flex-direction: row;
`;

const NewHolder = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.primaryGreen};
  width: 3.1em;
  height: 1.2em;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0.25em;
  margin-left: 1em;
`;
export const PaymentPlanHolder = styled.div`
  width: 100%;
  margin: 20px 0 0 0;
`;
export const PaymentPlanHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;
`;
export const PaymentPlanText = styled.div`
  width: 90%;
`;
export const PaymentPlanIcon = styled.div`
  width: 10%;
  text-align: right;
`;
export const ExpandButton = styled.button`
  border: none;
  background-color: white;
`;
export const Total = styled.div`
  width: 100%;
  text-align: right;
  display: inline-grid;
  padding: 10px;
`;
const TermsLink = styled.a`
  cursor: pointer;
  color: #1387ac;
  font-size: 16px;
  margin-bottom: 20px;
`;

interface BorderProps {
  width: number;
}

interface Props {
  onPayButton: () => void;
  singleInvoice: any;
  singleInvoicePDF: string | undefined;
  singleInvoiceAttachment: string | undefined;
}

export const getStatus = (invoice: SingleInvoice) => {
  const translate = useTranslate();
  switch (invoice?.statuses?.main) {
    case InvoiceStatuses.activated: {
      const invoiceisOverdue = dayjs(new Date()).isAfter(
        dayjs(invoice.dates.expireDate)
      );
      const daysDiff = dayjs(new Date()).diff(invoice.dates.expireDate, 'day');
      return invoiceisOverdue && daysDiff !== 0
        ? translate('Invoices.ActivatedPostDueDate', {
            days: Math.abs(dayjs().diff(invoice.dates.expireDate, 'day')),
          })
        : translate('Invoices.ActivatedPreDueDate', {
            days: Math.abs(
              dayjs(new Date()).diff(invoice.dates.expireDate, 'day')
            ),
          });
    }
    case InvoiceStatuses.closed: {
      return invoice.partPayment.isPartPayment
        ? translate('Invoices.Closed')
        : translate('Invoices.PaidOn', {
            date: invoice.dates.latestFinancialDate,
          });
    }
    case InvoiceStatuses.paused: {
      return invoice.partInvoices.length &&
        invoice.partInvoices[0].invoiceMainStatus === InvoiceStatuses.closed
        ? translate('Invoices.Closed')
        : translate('Invoices.Paused');
    }
    case InvoiceStatuses.disputed: {
      return translate('DebtorPortalStatuses.Disputed');
    }
    case InvoiceStatuses.stopped: {
      return translate('DebtorPortalStatuses.Stopped');
    }
    case 'PAID': {
      return 'DebtorPortalStatuses.Paid';
    }
    default: {
      return '';
    }
  }
};

const InvoicePageBody: React.FC<Props> = (props) => {
  const {
    onPayButton,
    singleInvoice,
    singleInvoicePDF,
    singleInvoiceAttachment,
  } = props;
  const translate = useTranslate();
  const isMobile = window.matchMedia('(max-width: 688px)').matches;
  const [expanded, setExpanded] = useState<boolean>(false);

  let totalCost;
  let totalFee;

  if (singleInvoice.partPayment.isPartPayment) {
    totalCost =
      +singleInvoice.partPaymentPlan[0].annuityAmount +
      +singleInvoice.partPaymentPlan[1].annuityAmount +
      +singleInvoice.partPaymentPlan[2].annuityAmount;
    totalFee =
      +singleInvoice.partPaymentPlan[0].annuityInterest +
      +singleInvoice.partPaymentPlan[1].annuityInterest +
      +singleInvoice.partPaymentPlan[2].annuityInterest;
  }

  const formatCurrency = (value: string) => {
    if (value) {
      const parts = value.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    }
    return '';
  };

  if (
    singleInvoice?.statuses?.main &&
    Object.keys(InvoiceStatuses).includes(
      singleInvoice.statuses.main.toLowerCase()
    )
  ) {
    return (
      <>
        <div style={{ marginTop: '7em' }}>
          <Typography
            fontSize={1}
            fontWeight={FontWeights.Thin}
            color={theme.colors.dark}
            style={{
              lineHeight: 1.5,
            }}
          >
            {`${translate('Invoices.Hello', {
              client: singleInvoice?.debtor?.name,
            })}`}
          </Typography>
        </div>
        <div style={{ marginTop: '0.5em', marginBottom: '2em' }}>
          <Typography
            fontSize={1}
            fontWeight={FontWeights.Thin}
            color={theme.colors.dark}
            style={{
              lineHeight: 1.5,
            }}
          >
            {`${translate('Invoices.HiDebtorThisIsClient', {
              debtor: singleInvoice?.debtor?.name,
              client: singleInvoice.clientName,
            })}`}
          </Typography>
        </div>
        <InvoiceBodyContainer>
          <InvoiceBodyInfoHolder width={0}>
            <InvoiceBodyTextHolder>
              <Typography fontSize={1} fontWeight={FontWeights.Normal}>
                {translate('Invoices.Amount')}
              </Typography>
              <Typography
                lineHeight={2}
                fontSize={1.2}
                fontWeight={FontWeights.Thick}
              >
                {`${
                  formatCurrency(singleInvoice?.totals?.totalToPay) || '-'
                } SEK`}
              </Typography>
            </InvoiceBodyTextHolder>

            <InvoiceImageHoder>
              {!singleInvoice.clientLogo.includes('null') && (
                <Image src={singleInvoice?.clientLogo} />
              )}
              <Typography color={theme.colors.secondaryGray} fontSize={0.9}>
                {singleInvoice?.clientName}
              </Typography>
            </InvoiceImageHoder>
          </InvoiceBodyInfoHolder>
          <InvoiceBodyInfoHolder width={0}>
            <Typography color={theme.colors.secondaryGray} fontSize={1}>
              {translate('Invoices.Status')}
            </Typography>
            <Typography fontWeight={500} fontSize={1}>
              {getStatus(singleInvoice)}
            </Typography>
          </InvoiceBodyInfoHolder>
          <InvoiceBodyInfoHolder width={1}>
            <Typography color={theme.colors.secondaryGray} fontSize={1}>
              {`${translate('Invoices.DueDate')}`}
            </Typography>
            <Typography fontSize={1}>
              {`${singleInvoice?.dates?.expireDate || '-'}`}
            </Typography>
          </InvoiceBodyInfoHolder>
          <InvoiceBodyInfoHolder width={1}>
            <Typography color={theme.colors.secondaryGray} fontSize={1}>
              {translate('Invoices.ReferenceNumber')}
            </Typography>
            <Typography fontSize={1}>
              {singleInvoice?.paymentReference || '-'}
            </Typography>
          </InvoiceBodyInfoHolder>
          <InvoiceBodyInfoHolder width={0}>
            <Typography color={theme.colors.secondaryGray} fontSize={1}>
              {translate('Invoices.InvoiceNumber')}
            </Typography>
            <Typography fontSize={1}>
              {singleInvoice?.invoiceNumber || '-'}
            </Typography>
          </InvoiceBodyInfoHolder>

          <InvoiceBodyInfoHolder width={0} />
          {singleInvoice?.statuses?.main.toLowerCase() === 'activated' &&
            singleInvoice.type !== 'PART_INVOICE' &&
            singleInvoice.manualCreditDecision &&
            dayjs(singleInvoice.dates.expireDate).diff(
              dayjs(Date.now()),
              'days',
              true
            ) > 3 && (
              <>
                <PayLaterHolder>
                  <Typography
                    lineHeight={2}
                    fontWeight={FontWeights.Thick}
                    fontSize={1.2}
                  >
                    {translate('Invoices.PayLater')}
                  </Typography>
                  <NewHolder>
                    <Typography
                      color={theme.colors.white}
                      fontWeight={FontWeights.Thin}
                      fontSize={0.8}
                    >
                      {translate('Invoices.New')}
                    </Typography>
                  </NewHolder>
                </PayLaterHolder>

                <Typography
                  color={theme.colors.secondaryGray}
                  lineHeight={1.4}
                  fontSize={1}
                >
                  {translate('Invoices.SplitInvoice')}
                </Typography>
                <InoviceBodyButtonHolder>
                  <Button
                    mb={1}
                    type={ButtonType.Secondary}
                    onClick={onPayButton}
                  >
                    {translate('Invoices.PayInParts')}
                  </Button>
                </InoviceBodyButtonHolder>
              </>
            )}
        </InvoiceBodyContainer>
        {singleInvoice.partPayment.isPartPayment && totalCost && totalFee && (
          <>
            <InvoiceBodyContainer>
              <PaymentPlanHolder>
                <PaymentPlanHeader>
                  <PaymentPlanText>
                    {translate('Invoices.PartPaymentPlan')}
                  </PaymentPlanText>
                  <PaymentPlanIcon>
                    <ExpandButton onClick={() => setExpanded(!expanded)}>
                      <StyledIcon
                        color="#444444"
                        height="1.4em"
                        icon={expanded ? SvgSrc.ChevronUp : SvgSrc.ChevronDown}
                      />
                    </ExpandButton>
                  </PaymentPlanIcon>
                </PaymentPlanHeader>
                {expanded && (
                  <>
                    <Card
                      instalment={singleInvoice.partPaymentPlan[0]}
                      isOnInvoiceView
                    />
                    <Card
                      instalment={singleInvoice.partPaymentPlan[1]}
                      isOnInvoiceView
                    />
                    <Card
                      instalment={singleInvoice.partPaymentPlan[2]}
                      isOnInvoiceView
                    />
                    <Total>
                      <Typography fontWeight={FontWeights.Thick} fontSize={1.6}>
                        {translate('Invoices.TotalCost')}{' '}
                        {formatAmount(totalCost)}
                      </Typography>
                      <Typography fontWeight={FontWeights.Thin} fontSize={0.8}>
                        {`(${translate(
                          'Invoices.IncludesATotalFeeOf'
                        )} ${formatAmount(totalFee)})`}
                      </Typography>
                      <TermsLink href="https://www.qred.se/info/villkor-delbetalning">
                        {' '}
                        {translate('Invoices.TermsAndConditions')}.
                      </TermsLink>
                    </Total>
                  </>
                )}
              </PaymentPlanHolder>
            </InvoiceBodyContainer>
          </>
        )}
        <InvoiceBodyContainer>
          <InvoicePDF singleInvoicePDF={singleInvoicePDF} isMobile={isMobile} />
        </InvoiceBodyContainer>
        {singleInvoiceAttachment && (
          <InvoiceBodyContainer>
            <InvoicePDF
              singleInvoicePDF={singleInvoiceAttachment}
              isMobile={isMobile}
            />
          </InvoiceBodyContainer>
        )}
      </>
    );
  }
  return <></>;
};

export default InvoicePageBody;
